'use strict';

/**
 * Seed the AnswerStore from the API response. RPI-984.
 *
 * The response carries answers on two channels. `init_state.form_data` is what
 * has been RECORDED. `renderables[].question.answer` is what the widget renders
 * as SELECTED — a questionnaire default has no `form_data` key at all, because
 * nothing has been recorded yet, which is why seeding from `form_data` alone
 * left `store-missing` in the RR-4 shadow compare.
 *
 * `ifans` resolves `question.answer` before falling back to `form_data`.
 * Copying `form_data` in first and letting `question.answer` overwrite it
 * reproduces that precedence, so nothing here re-implements the helper.
 *
 * Values are strings because that is what a form submits, measured rather than
 * assumed: `answer: 1` renders as `value="1"` and reads back as `"1"`, and a
 * checkbox carries no `value` attribute in `_response-item.hbs` /
 * `_response-button.hbs`, so the browser reports `"on"`.
 *
 * One deliberate difference from the template: `ifans` compares codes with
 * `===`, so a backend sending `answer: "1"` for `code: 1` renders nothing
 * selected. The seed takes the answer at face value instead. If that ever
 * happens it is a backend bug, and the store holding the intended answer is the
 * better failure.
 *
 * SCOPE — this module and `seedHiddenAnswers()` in `answer-sync.js` are
 * COMPLEMENTARY, not substitutes. This one covers `question.answer` on visible
 * controls, which nothing else could see. That one covers hidden inputs across
 * every answer channel, including the ones no response field can express:
 * nested `category_options[].answer` (Scale, Placement), template literals
 * (`imagemap` "unselected", `audio`/`video` "1") and template-computed counts
 * (`matrix` row_count). The two overlap only on `passthru`. Deleting either
 * loses coverage — see `aiDocs/RPI-984_REMEDIATION_PLAN.md` §1-2.
 *
 * Response only: nothing here reads the DOM.
 */

// What a checked checkbox submits when it has no `value` attribute.
var CHECKBOX_ON = 'on';

// What an UNchecked checkbox submits: `get_form_value` returns '' for it
// (globals/form.js), and `events.js` keeps that '' (it is not null). A
// non-selected `multiple` option must seed '' — not be omitted — to reproduce
// the submitted payload and to overwrite any stale 'on' copied from form_data.
var CHECKBOX_OFF = '';

function hasOwn(obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key);
}

/**
 * The variable a question actually submits under.
 *
 * Nearly every widget submits under `input_id`, but not all: `rule.hbs:26`
 * names its control `{{response_id}}` and keeps `input_id` for the wrapper's
 * `id=`. Resolving against `res.variables` — the server's own list of what this
 * page submits — picks the right one without the seeder knowing about widgets.
 *
 * @param {Array<string>} declared `res.variables`.
 * @param {Array<*>} candidates Names to try, most likely first.
 * @returns {?string} The declared name, or `null` if none of them is declared.
 */
function pickDeclaredKey(declared, candidates) {
  var i;

  // No declared list — fall back to the first candidate, which is `input_id`
  // for every widget but Rule. Do NOT "simplify" this away: `init_page_state`
  // is called with responses that carry no `variables` key (see the spec), and
  // without this the seeder would resolve no key at all and write nothing.
  if (!declared.length) {
    for (i = 0; i < candidates.length; i++) {
      if (candidates[i]) {
        return candidates[i];
      }
    }
    return null;
  }

  for (i = 0; i < candidates.length; i++) {
    if (candidates[i] && declared.indexOf(candidates[i]) !== -1) {
      return candidates[i];
    }
  }
  return null;
}

/**
 * Write the variables one question's `answer` contributes.
 *
 * @param {Object} out Accumulating answer map, mutated.
 * @param {Object} question The renderable's `question`.
 * @param {Array<string>} declared `res.variables`.
 * @param {Array<string>} derived Keys this seeder wrote, mutated.
 */
function seedAnswer(out, question, declared, derived) {
  var key;

  // `multiple`: each response option is its own checkbox variable named by
  // `input_name`. Mirror exactly what the template renders as checked, because
  // submit scrapes that. `_response-item.hbs` gates the box on
  // `{{#ifans answer input_name code}}`, and `ifans`
  // (`handlebars-helpers-data.js`) checks TWO sources, not one:
  //
  //   1. `$.inArray(code, answer)` — STRICT membership in the answer list.
  //   2. `ifanswer` — a recorded `form_data[input_name]` (e.g. 'on'), which can
  //      render an option checked even when the answer list omits it.
  //
  // So DO NOT treat `answer` as the complete selection: a recorded option must
  // stay. Seed '' only for an option that is neither in `answer` (strict, to
  // match `$.inArray`) nor recorded in `form_data` — those were previously
  // absent, which read as `store-missing` against the DOM's unchecked ''.
  // Matching is strict on purpose: a numeric `answer: [1]` does NOT select a
  // string `code: "1"`, because `$.inArray` would not either (RPI-1031).
  if (Array.isArray(question.answer)) {
    var options = question.response_options || [];
    for (var i = 0; i < options.length; i++) {
      var name = options[i].input_name;
      if (!name) {
        continue;
      }
      if (question.answer.indexOf(options[i].code) !== -1) {
        out[name] = CHECKBOX_ON;
        derived.push(name);
      } else if (!hasOwn(out, name)) {
        // Not selected and not recorded: the DOM renders it unchecked and
        // submits ''. A recorded `form_data` value is left untouched — the
        // template's `ifanswer` fallback may still render it checked.
        out[name] = CHECKBOX_OFF;
        derived.push(name);
      }
    }
    return;
  }

  // single, dropdown, open: one variable holding the code or the text.
  key = pickDeclaredKey(declared, [question.input_id, question.response_id]);
  if (key === null) {
    return;
  }
  out[key] = String(question.answer);
  derived.push(key);
}

/**
 * The page's initial answer state.
 *
 * @param {Object} res The API response.
 * @param {Array<string>} [derivedOut] If given, receives every key this
 *     function DERIVED — `passthru` entries and `seedAnswer` output, but not
 *     the `form_data` copy. `strayKeys` needs that distinction.
 * @returns {Object} Map of variable name to the value the form would submit.
 */
export function buildInitialAnswers(res, derivedOut) {
  var init = (res && res.init_state) || {};
  var formData = init.form_data || {};
  var renderables = init.renderables || [];
  var declared = (res && res.variables) || [];
  var derived = derivedOut || [];
  var out = {};
  var name;

  for (name in formData) {
    if (hasOwn(formData, name)) {
      out[name] = formData[name];
    }
  }

  for (var i = 0; i < renderables.length; i++) {
    var question = renderables[i] && renderables[i].question;
    if (!question) {
      continue;
    }

    // `passthru` is the flat {variable: value} map the widget renders as hidden
    // inputs (`{{#eachkeys passthru}}`), so it needs no expansion. Note this is
    // the ONLY hidden-input channel the response can express — the rest are
    // recovered by `seedHiddenAnswers()`, which is why that function still
    // exists alongside this one.
    var passthru = question.passthru || {};
    for (name in passthru) {
      if (hasOwn(passthru, name)) {
        // Handlebars renders `null` as an empty string, so the hidden input
        // submits '' — match that rather than the literal 'null'.
        out[name] =
          passthru[name] === null || passthru[name] === undefined
            ? ''
            : String(passthru[name]);
        derived.push(name);
      }
    }

    if (question.answer !== null && question.answer !== undefined) {
      seedAnswer(out, question, declared, derived);
    }
  }

  return out;
}

/**
 * Declared variables the seed could not account for.
 *
 * `res.variables` is the authoritative list of what this page submits, so it is
 * the completeness oracle — no DOM read needed. Expect entries here for widgets
 * whose answer state is not a plain `question.answer` (grid, rank, cardsort),
 * and for the unselected options of a `multiple` that has never been answered.
 *
 * @param {Object} res The API response.
 * @param {Object} answers The map returned by `buildInitialAnswers`.
 * @returns {Array<string>} Variable names with no seeded value.
 */
export function unseededVariables(res, answers) {
  var declared = (res && res.variables) || [];
  var missing = [];
  for (var i = 0; i < declared.length; i++) {
    if (!hasOwn(answers, declared[i])) {
      missing.push(declared[i]);
    }
  }
  return missing;
}

/**
 * Keys the seeder derived that this page does not submit.
 *
 * The inverse of `unseededVariables`, and the loud-failure half of key
 * resolution: a MISSING key shows up there, a WRONG key shows up here. A
 * key-derivation bug that lands on `input_id` where the control is named
 * `response_id` produces both at once, which is what made the Rule case
 * invisible.
 *
 * Deliberately computed over DERIVED keys only, never the `form_data` copy:
 * `form_data` is the server's own snapshot and legitimately carries answers
 * from earlier pages, which are not this page's declared variables and not the
 * seeder's bug to report.
 *
 * @param {Object} res The API response.
 * @param {Array<string>} derived Keys collected by `buildInitialAnswers`.
 * @returns {Array<string>} Derived keys absent from `res.variables`.
 */
export function strayKeys(res, derived) {
  var declared = (res && res.variables) || [];
  var stray = [];
  var seen = {};

  // Nothing declared means nothing to contradict — the fallback in
  // `pickDeclaredKey` is doing the work, and reporting every key as stray would
  // be noise, not a finding.
  if (!declared.length) {
    return stray;
  }

  for (var i = 0; i < (derived || []).length; i++) {
    var name = derived[i];
    if (declared.indexOf(name) === -1 && !hasOwn(seen, name)) {
      seen[name] = true;
      stray.push(name);
    }
  }
  return stray;
}
